/**
 * ПОИСК ПО СОДЕРЖИМОМУ СООБЩЕНИЙ — серверный, через Matrix CS-API.
 *
 * ПОЧЕМУ СЕРВЕР, А НЕ ЛОКАЛЬНЫЙ ИНДЕКС (решение 2026-09-24, жалоба Антона
 * «поиск по контенту на вэбе тоже не работает»):
 *  - в браузере индекс держать негде: IndexedDB-индекс по всей истории нужно
 *    сначала прогреть, то есть скачать всю историю всех комнат;
 *  - E2EE у нас ВЫКЛЮЧЕН, сервер видит открытые тексты и умеет искать сам;
 *  - серверный поиск сразу покрывает ВСЮ историю, включая то, что клиент
 *    никогда не загружал.
 *
 * ЭНДПОИНТ: POST /_matrix/client/v3/search, категория `room_events`.
 * ПРОВЕРЕНО ЖИВЬЁМ на matrix1.algsuccess.com 2026-09-24 (curl, токен Momo):
 *   ответ = { search_categories: { room_events: {
 *              results: [{ rank, result: <event with room_id>, context: {…} }],
 *              count: 34, highlights: ["привет"], next_batch: "YWxsCgox…" } } }
 *   пустой результат: { results: [], count: 0, highlights: [] } — БЕЗ next_batch;
 *   пустой search_term ошибки НЕ даёт, отдаёт пустой список (поэтому короткие
 *   запросы отсекаем на клиенте, чтобы не ходить на сервер зря);
 *   пагинация: тот же body + `?next_batch=<токен>` — отдаёт следующую порцию
 *   и НОВЫЙ next_batch. Размер порции задаётся `filter.limit` (по умолчанию 10).
 *
 * matrix-js-sdk 42.3.0: `client.search({ body, next_batch }, abortSignal)`
 * (см. node_modules/matrix-js-sdk/lib/client.d.ts). Сигнатура сверена с кодом
 * пакета, не додумана. Берём именно сырой `search`, а не `searchRoomEvents`:
 * последний жёстко зашивает order_by/event_context и заворачивает ответ в
 * модель SearchResult, которая нам здесь не нужна.
 */
import type { MatrixClient } from "matrix-js-sdk";
import { SearchOrderBy } from "matrix-js-sdk/lib/@types/search";
import type { IEventWithRoomId, ISearchRequestBody } from "matrix-js-sdk/lib/@types/search";

/** Сколько результатов просим за один заход (и за одно «показать ещё»). */
export const SEARCH_PAGE_SIZE = 20;

/** Короче этого не ищем: односимвольный запрос вернёт пол-истории. */
export const SEARCH_MIN_LEN = 2;

/** Одно найденное сообщение в виде, готовом для отрисовки. */
export interface MessageHit {
  eventId: string;
  roomId: string;
  /** Имя комнаты из локального клиента; null — комната клиенту неизвестна. */
  roomName: string | null;
  sender: string;
  /** Отображаемое имя автора (из profile_info ответа или из комнаты). */
  senderName: string;
  /** origin_server_ts, мс. */
  ts: number;
  /** Текст сообщения (content.body). */
  body: string;
}

/** Страница результатов поиска. */
export interface MessageSearchPage {
  hits: MessageHit[];
  /** Токен следующей порции; undefined — больше ничего нет. */
  nextBatch?: string;
  /** Сколько всего совпадений нашёл сервер (может отсутствовать). */
  count?: number;
  /** Термины, которые сервер считает совпавшими — ими подсвечиваем. */
  highlights: string[];
}

/** Достать текст из события; не-текстовые события отсеиваем выше. */
function bodyOf(ev: IEventWithRoomId): string {
  const content = ev.content as { body?: unknown } | undefined;
  return typeof content?.body === "string" ? content.body : "";
}

/**
 * Один запрос к серверу. `nextBatch` — продолжение предыдущего.
 * `signal` обязателен по смыслу: быстрый набор рождает устаревшие запросы,
 * и ответ на них не должен затирать свежий результат.
 */
export async function searchMessages(
  client: MatrixClient,
  term: string,
  opts: { nextBatch?: string; signal?: AbortSignal } = {},
): Promise<MessageSearchPage> {
  const body: ISearchRequestBody = {
    search_categories: {
      room_events: {
        search_term: term,
        // "recent": свежее сверху. Ранжирование по релевантности ("rank")
        // в мессенджере сбивает с толку — человек ищет «тот разговор на
        // прошлой неделе», а не самое частотное совпадение.
        order_by: SearchOrderBy.Recent,
        filter: { limit: SEARCH_PAGE_SIZE },
        // Контекст не запрашиваем (before/after = 0): в списке результатов
        // показывается одна строка, а соседние события — лишний трафик на
        // каждую страницу. include_profile нужен ради имени автора.
        event_context: { before_limit: 0, after_limit: 0, include_profile: true },
        include_state: false,
      },
    },
  };

  const res = await client.search({ body, next_batch: opts.nextBatch }, opts.signal);
  const cat = res?.search_categories?.room_events ?? {};
  const raw = cat.results ?? [];

  const hits: MessageHit[] = [];
  for (const item of raw) {
    const ev = item?.result;
    if (!ev || ev.type !== "m.room.message") continue;
    const text = bodyOf(ev);
    if (!text) continue;
    const room = client.getRoom(ev.room_id);
    const profile = item.context?.profile_info?.[ev.sender];
    const member = room?.getMember(ev.sender);
    hits.push({
      eventId: ev.event_id,
      roomId: ev.room_id,
      roomName: room?.name ?? null,
      sender: ev.sender,
      senderName: profile?.displayname || member?.name || ev.sender,
      ts: ev.origin_server_ts,
      body: text,
    });
  }

  return {
    hits,
    nextBatch: cat.next_batch,
    count: cat.count,
    highlights: cat.highlights ?? [],
  };
}

/**
 * Разбить текст на куски для подсветки: чётные индексы — обычный текст,
 * нечётные — совпадение. Сервер отдаёт СЛОВА-совпадения (`highlights`), а не
 * позиции, поэтому ищем их сами, без учёта регистра.
 *
 * Запасной вариант — сама строка запроса: если сервер highlights не прислал
 * (так бывает при поиске по подстроке), подсветить всё равно надо.
 */
export function splitHighlights(text: string, terms: string[]): { text: string; hit: boolean }[] {
  const needles = terms.map((t) => t.trim()).filter((t) => t.length > 0);
  if (needles.length === 0) return [{ text, hit: false }];

  const lower = text.toLowerCase();
  const marks: [number, number][] = [];
  for (const needle of needles) {
    const n = needle.toLowerCase();
    let from = 0;
    for (;;) {
      const at = lower.indexOf(n, from);
      if (at < 0) break;
      marks.push([at, at + n.length]);
      from = at + n.length;
    }
  }
  if (marks.length === 0) return [{ text, hit: false }];

  marks.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const m of marks) {
    const last = merged[merged.length - 1];
    if (last && m[0] <= last[1]) last[1] = Math.max(last[1], m[1]);
    else merged.push([m[0], m[1]]);
  }

  const out: { text: string; hit: boolean }[] = [];
  let pos = 0;
  for (const [start, end] of merged) {
    if (start > pos) out.push({ text: text.slice(pos, start), hit: false });
    out.push({ text: text.slice(start, end), hit: true });
    pos = end;
  }
  if (pos < text.length) out.push({ text: text.slice(pos), hit: false });
  return out;
}

/**
 * Обрезать длинное сообщение вокруг первого совпадения: в строку списка
 * влезает ~160 символов, а найтись может слово в конце «простыни».
 */
export function snippetAround(text: string, terms: string[], width = 160): string {
  const flat = text.replace(/\s+/g, " ").trim();
  if (flat.length <= width) return flat;

  const lower = flat.toLowerCase();
  let at = -1;
  for (const term of terms) {
    const n = term.trim().toLowerCase();
    if (!n) continue;
    const found = lower.indexOf(n);
    if (found >= 0 && (at < 0 || found < at)) at = found;
  }
  if (at < 0) return flat.slice(0, width) + "…";

  const start = Math.max(0, at - Math.floor(width / 3));
  const end = Math.min(flat.length, start + width);
  return (start > 0 ? "…" : "") + flat.slice(start, end) + (end < flat.length ? "…" : "");
}
